import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Search, Loader2, Check } from "lucide-react";
import { categories } from "../mock";
import { fetchListDetail, adminUpdateList } from "../api";
import { toast } from "sonner";

const catColor = (id) => categories.find((c) => c.id === id)?.color;

const AdminListDialog = ({ open, onOpenChange, list, allPlaces, onSaved }) => {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!list || !open) return;
      setLoading(true);
      try {
        const detail = await fetchListDetail(list.id);
        setLabel(detail.label);
        setDescription(detail.description || "");
        setSelected(detail.places.map((p) => p.id));
      } catch (e) {
        toast.error("could not load list.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [list, open]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return allPlaces;
    return allPlaces.filter(
      (p) => p.name.toLowerCase().includes(f) || p.area.toLowerCase().includes(f)
    );
  }, [allPlaces, filter]);

  const save = async () => {
    if (!label.trim()) return toast.error("list needs a name.");
    setSaving(true);
    try {
      await adminUpdateList(list.id, {
        label: label.trim(),
        description: description.trim(),
        place_ids: selected,
      });
      toast.success("list updated.");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save list.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Edit list</DialogTitle>
          <DialogDescription>amend the name, blurb and the spots in this list.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-neutral-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> loading…
          </div>
        ) : (
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>List name</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>One-liner</Label>
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <div className="flex items-center justify-between">
                <Label>Spots</Label>
                <span className="text-xs font-medium text-neutral-500">{selected.length} selected</span>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter spots…" className="h-9 pl-9 text-sm" />
              </div>
              <div className="mt-1 max-h-52 overflow-y-auto rounded-xl border border-neutral-200">
                {visible.map((p) => {
                  const isSel = selected.includes(p.id);
                  return (
                    <button type="button" key={p.id} onClick={() => toggle(p.id)}
                      className={`flex w-full items-center gap-3 border-b border-neutral-100 px-3 py-2 text-left last:border-0 ${isSel ? "bg-neutral-50" : "hover:bg-neutral-50"}`}>
                      <Checkbox checked={isSel} className="pointer-events-none" />
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: catColor(p.category) }} />
                      <span className="flex-1 truncate text-sm text-neutral-800">{p.name}</span>
                      <span className="shrink-0 text-xs text-neutral-400">{p.area}</span>
                      {isSel && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-neutral-900 text-white hover:bg-neutral-800">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminListDialog;
